<script setup lang="ts">
import { createScheduleFromCalendarResponse } from '@/components/Schedule/ScheduleHelper'
import {
  isVideoConferenceKind,
  meetingKindFromStaffDefault,
} from '@/constants/staffMeetingLocationOptions'
import { useCurrency } from '@/composables/useCurrency'
import AvailabilityPicker from '@/components/Settings/AvailabilityPicker.vue'
import SchedulePicker from '@/components/Settings/SchedulePicker.vue'
import calendarScheduleState from '@/states/calendarScheduleState'
import {
  MinusIcon,
  PlusIcon,
  Settings01Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  AllExtensions,
  RequiredExtensions,
  UIRichTextEditor,
} from '@gohighlevel/ghl-text-editor'
import {
  UIButton,
  UIForm,
  UIFormItem,
  UIInput,
  UIInputGroup,
  UIInputGroupLabel,
  UIInputNumber,
  UIModal,
  UIModalHeader,
  UISelect,
  UISwitch,
} from '@gohighlevel/ghl-ui'
import { History } from '@tiptap/extension-history'
import { Editor } from '@tiptap/vue-3'
import DOMPurify from 'dompurify'
import { debounce, isEmpty } from 'lodash-es'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { CalendarService } from '../../class/services/CalendarService'
import { ConnectionService } from '../../class/services/ConnectionService'
import Calendar, {
  CalendarType,
  Stripe,
  TeamMember,
} from '../../models/calendar'
import AppState, { resetCalendarEditValidationData } from '../../states/app'
import { toggleAdvancedView } from '../../utils/commonModalFunctions'
import { ALLOWED_ATTR_FOR_DESCRIPTION } from '../../utils/constants'
import {
  getDefaultTeamMember,
  getTooltip,
  isPaymentAllowed,
} from '../../utils/generalFunctions'
import TextEditorMenu from '../common/TextEditorMenu.vue'
import { Schedule } from '../Schedule/Schedule'
import { useAvailabilityText } from '../Schedule/ScheduleHelper'

const getAvailabilityText = useAvailabilityText()
const { t } = useI18n()
const { currencyOptions: paymentCurrencyOptions } = useCurrency()

const props = defineProps({
  show: Boolean,
  selectedCalendarType: {
    type: String,
    default: CalendarType.EVENT,
  },
})

const availabilityPickerRef = ref()
const formRef = ref()
const editor = ref<any>(null)
const isInitialLoad = ref(true)

const defaultFormData = {
  name: '',
  officeHours: [],
  meetingLocation: '',
  locationConfigurations: [
    {
      kind: 'custom',
      location: '',
      position: 0,
    },
  ],
  teamMembers: [],
  appoinmentPerSlot: '1',
  description: '',
  slotDuration: '30',
  slug: '',
  slotDurationUnit: 'mins',
  stripe: {
    amount: 0,
    currency: 'USD',
    chargeDescription: '',
  },
}

const state = reactive({
  isSaving: false,
  isSlugValidating: false,
  isGeneratingSlug: false,
  hasUserModifiedSlug: false,
  slugValidationResult: null as any,
  shouldShowDescription: false,
  shouldAcceptPayment: false,
  zoomConnections: [] as any,
  formData: { ...defaultFormData },
  msTeamsConnections: [] as any,
})

const slotDurationUnitOptions = computed(() => {
  return [
    {
      value: 'mins',
      label: t('settings.availability.schedule.mins'),
    },
    {
      value: 'hours',
      label: t('settings.availability.schedule.hours'),
    },
  ]
})

const shouldAllowPayment = computed(() => {
  return AppState.shouldAllowPayment
})

const rules = computed(() => {
  return {
    name: {
      validator() {
        if (!state.formData.name?.trim()) {
          return new Error(t('calendar_advanced.meeting_details.enter_a_name'))
        }

        return true
      },
      message: t('calendar_advanced.meeting_details.enter_a_name'),
      trigger: 'blur',
    },
    slotDuration: {
      validator(_, value) {
        const slotDurationInMins =
          state.formData.slotDurationUnit === 'hours'
            ? Number(value) * 60
            : Number(value)

        if (!value) {
          return new Error(t('validation_messages.slot_duration_required'))
        } else if (!/^\d*$/.test(value)) {
          return new Error(t('validation_messages.slot_duration_integer'))
        } else if (slotDurationInMins < 1 || slotDurationInMins > 12 * 60) {
          return new Error(t('validation_messages.meeting_duration_validation'))
        }
        return true
      },
      trigger: ['blur', 'input'],
    },
    appoinmentPerSlot: {
      validator(_, value) {
        if (props.selectedCalendarType !== CalendarType.CLASS_BOOKING) {
          return true
        }

        if (!value) {
          return new Error(t('validation_messages.seats_class_required'))
        } else if (!/^\d*$/.test(value)) {
          return new Error(t('validation_messages.seats_class_integer'))
        } else if (Number(value) > 9999 || Number(value) < 0) {
          return new Error(t('validation_messages.seats_class_validation'))
        }
        return true
      },
      trigger: ['blur', 'input'],
    },
    meetingLocation: {
      validator(_, value) {
        if (props.selectedCalendarType !== CalendarType.CLASS_BOOKING) {
          return true
        }

        if (!value) {
          return new Error(t('validation_messages.meeting_location_required'))
        }
        return true
      },
      trigger: ['blur', 'input'],
    },
    teamMembers: {
      validator() {
        if (props.selectedCalendarType === CalendarType.EVENT) {
          return true
        }

        if (
          props.selectedCalendarType === CalendarType.CLASS_BOOKING ||
          props.selectedCalendarType === CalendarType.PERSONAL
        ) {
          if (state.formData.teamMembers.length > 1) {
            return new Error(
              t(
                'calendar_advanced.meeting_details.only_one_team_member_allowed'
              )
            )
          }
        }

        if (props.selectedCalendarType === CalendarType.COLLECTIVE) {
          if (state.formData.teamMembers.length < 2) {
            return new Error(
              t(
                'calendar_advanced.meeting_details.at_least_two_team_members_required'
              )
            )
          }
        }

        if (state.formData.teamMembers.length === 0) {
          return new Error(
            t(
              'calendar_advanced.meeting_details.at_least_one_team_member_required'
            )
          )
        }
        return true
      },
      trigger: ['input', 'blur'],
    },
    slug: {
      required: true,
      trigger: ['input'],
      asyncValidator() {
        state.isSlugValidating = true

        return new Promise(async (resolve, reject) => {
          if (!state.formData.slug) {
            state.isSlugValidating = false
            state.slugValidationResult = {
              isValid: false,
              message: t('validation_messages.slug_required'),
            }
            return resolve(true)
          }

          const slugRegex = /^[a-zA-Z0-9-]+(\/[a-zA-Z0-9-]+)*$/

          if (!slugRegex.test(state.formData.slug)) {
            state.isSlugValidating = false
            state.slugValidationResult = {
              isValid: false,
              message: t('validation_messages.slug_not_valid'),
            }
            return resolve(true)
          }

          try {
            const { data } = await CalendarService.isValidCalendarSlug(
              state.formData.slug
            )
            if (!data.available) {
              state.slugValidationResult = {
                isValid: false,
                message: t('validation_messages.slug_already_taken'),
              }
              return resolve(true)
            }
          } catch (error) {
            state.slugValidationResult = {
              isValid: false,
              message: t('validation_messages.error_validating_slug'),
            }
            return resolve(true)
          }

          state.isSlugValidating = false
          state.slugValidationResult = { isValid: true }
          return resolve(true)
        })
      },
    },
    stripeAmount: {
      trigger: ['input', 'blur'],
      validator() {
        if (!state.formData.stripe.amount) {
          return true
        }
        if (state.formData.stripe.amount.toString().match(/^\d+(\.\d{3,})$/)) {
          return new Error(t('calendar_advanced.forms_payment.stripe_error'))
        }
        return true
      },
    },
  }
})

const usersInLocationOptions = computed(() => {
  return (AppState.globalUsers as any)?.users?.map((x: any) => {
    return {
      value: x.id,
      label: x.name,
      disabled:
        (props.selectedCalendarType === CalendarType.CLASS_BOOKING ||
          props.selectedCalendarType === CalendarType.PERSONAL) &&
        state.formData?.teamMembers?.length === 1 &&
        !state.formData?.teamMembers?.some(y => y === x.id),
    }
  })
})
const teamMembersDefaultSchedule = ref<Record<string, Schedule>>({})
const emit = defineEmits(['onConfirm', 'onModalClose', 'update:show'])

const methods = {
  /**
   * Load data from the server
   */
  async loadData() {
    await Promise.all([
      methods.loadConnections(),
      methods.loadPaymentInfo(),
      methods.loadDefaultSchedules(),
    ])
  },

  /**
   * Generate calendar slug based on calendar name
   */
  async generateSlugFromName(calendarName: string) {
    // Only generate slug if:
    // 1. Calendar name exists
    // 2. Either user hasn't manually modified the slug OR the slug is currently empty
    // 3. This is a new calendar creation (not editing existing)
    if (
      !calendarName?.trim() ||
      (state.hasUserModifiedSlug && state.formData.slug?.trim())
    ) {
      return
    }

    try {
      state.isGeneratingSlug = true
      const { data } = await CalendarService.generateCalendarSlug(
        calendarName.trim()
      )

      // Update slug if we got a valid response and either:
      // - User hasn't manually modified it, OR
      // - The slug field is currently empty
      if (
        data?.generatedSlug &&
        (!state.hasUserModifiedSlug || !state.formData.slug?.trim())
      ) {
        state.formData.slug = data.generatedSlug
        state.slugValidationResult = null
        rules.value.slug.asyncValidator()
      }
    } catch (error) {
      console.error('Error generating slug:', error)
    } finally {
      state.isGeneratingSlug = false
    }
  },
  async loadConnections() {
    const response = await ConnectionService.getConnections(AppState.locationId)
    const zoomConnections = response?.data?.locationConnections?.zoom || []
    const msTeamsConnections =
      response?.data?.locationConnections?.ms_teams || []

    state.msTeamsConnections = msTeamsConnections
    state.zoomConnections = zoomConnections
  },
  async loadPaymentInfo() {
    await isPaymentAllowed(AppState.locationId)
  },

  async loadDefaultSchedules() {
    const response = await CalendarService.getCalendarSchedules({
      locationId: AppState.locationId,
      isDefault: true,
    })
    const data = (response?.data?.schedules || []).filter(
      (x: any) => x.isDefault
    )
    data.forEach(x => {
      teamMembersDefaultSchedule.value[x.userId] = x as Schedule
    })
  },

  processTeamMemberInfo(teamMemberDetail, userId, index) {
    if (!teamMemberDetail) {
      return getDefaultTeamMember(userId)
    }

    const zoomConnection = state.zoomConnections.find(
      connection => connection.ownerId === userId
    )

    const selectedCustomMeetingLocation =
      teamMemberDetail.locationWiseMeetingLocation &&
      teamMemberDetail.locationWiseMeetingLocation[AppState.locationId]
    const selectedIsZoomAdded =
      teamMemberDetail.locationWiseZoomAdded &&
      teamMemberDetail.locationWiseZoomAdded[AppState.locationId]

    const teamMember = {
      userId: userId,
      priority: 0.5,
      isZoomAdded:
        props.selectedCalendarType === CalendarType.CLASS_BOOKING
          ? 'false'
          : selectedIsZoomAdded || 'false',
      zoomOauthId: zoomConnection?.oauthId || '',
      meetingLocation:
        state.formData.meetingLocation || selectedCustomMeetingLocation,
      selected: true,
    } as TeamMember
    if (props.selectedCalendarType === CalendarType.COLLECTIVE) {
      teamMember.isPrimary = index === 0
      teamMember.useMeetingForCollective = index === 0
    }
    return teamMember
  },

  /**
   * Save data to the server
   */
  async handleSave() {
    try {
      await formRef.value.getForm().validate()
    } catch (error) {
      state.isSaving = false
      return
    }
    if (!state.slugValidationResult?.isValid) {
      state.isSaving = false
      return
    }

    state.isSaving = true

    const hours = availabilityPickerRef.value?.methods?.getAvailability()

    state.formData.description = state.shouldShowDescription
      ? state.formData.description
      : ''

    const sanitizedDescription = DOMPurify.sanitize(
      state.formData.description,
      {
        ADD_ATTR: ALLOWED_ATTR_FOR_DESCRIPTION,
      }
    )
    const payload: Partial<Calendar> = {
      name: state.formData.name,
      description: sanitizedDescription,
      openHours: props.selectedCalendarType === CalendarType.EVENT ? hours : [],
      slotDuration:
        state.formData.slotDurationUnit === 'hours'
          ? Number(state.formData.slotDuration) * 60
          : Number(state.formData.slotDuration),
      slotDurationUnit: state.formData.slotDurationUnit,
      widgetSlug: state.formData.slug,
      locationId: AppState.locationId,
      calendarType: props.selectedCalendarType as CalendarType,
      showSeatsPerSlot:
        props.selectedCalendarType === CalendarType.CLASS_BOOKING,
    }

    if (props.selectedCalendarType === CalendarType.EVENT) {
      // payload.meetingLocation = state.formData.meetingLocation
      payload.locationConfigurations = [
        {
          kind: 'custom',
          location: state.formData.meetingLocation,
          position: 0,
        },
      ]
    }

    if (state.shouldAcceptPayment) {
      payload.stripe = {
        amount: Number(state.formData.stripe.amount),
        currency: state.formData.stripe.currency,
        chargeDescription: state.formData.stripe.chargeDescription,
      } as Stripe
      payload.isLivePaymentMode = true
    } else {
      payload.stripe = {} as Stripe
    }

    if (
      Array.isArray(state.formData.teamMembers) &&
      state.formData.teamMembers.length > 0
    ) {
      payload.teamMembers = state.formData.teamMembers.map((x: string, ind) => {
        const teamMemberDetail = (AppState.globalUsers as any)?.users.find(
          obj => obj.id === x
        )
        return methods.processTeamMemberInfo(teamMemberDetail, x, ind)
      }) as TeamMember[]
    }

    if (props.selectedCalendarType === CalendarType.CLASS_BOOKING) {
      payload.appoinmentPerSlot = Number(state.formData.appoinmentPerSlot)
    }

    if (props.selectedCalendarType === CalendarType.SERVICE) {
      payload.enableStaffSelection = true
      payload.slotInterval = 15
      payload.slotIntervalUnit = 'mins'
    }


    if (isEmpty(payload.openHours)) {
      delete payload.openHours
    }

    const { data } = await CalendarService.create(payload)
    if (props.selectedCalendarType === CalendarType.EVENT) {
      //create schedule from the open hours
      const schedule = createScheduleFromCalendarResponse(data.calendar)
      try {
        await calendarScheduleState.createSchedule(schedule.toJSON())
      } catch (error) {
        console.error(error)
      }
    } else {
      //update calendar id in the team members default schedules
      await Promise.all(
        state.formData.teamMembers.map(async (teamMemberId: string) => {
          if (!teamMembersDefaultSchedule.value[teamMemberId]) {
            return
          }
          const schedule = new Schedule(
            teamMembersDefaultSchedule.value[teamMemberId]
          )
          schedule.addCalendarId(data.calendar.id)
          return CalendarService.updateCalendarSchedule(schedule.id, {
            rules: schedule.rules,
            name: schedule.name,
            timezone: schedule.timezone,
            calendarIds: schedule.calendarIds,
          })
        })
      )
    }

    state.isSaving = false

    emit('onConfirm', data.calendar)

    methods.resetInputs()

    return data.calendar.id
  },
  resetInputs() {
    state.formData = {
      ...defaultFormData,
      stripe: {
        amount: 0,
        currency: 'USD',
        chargeDescription: '',
      },
    }

    state.shouldShowDescription = false
    state.shouldAcceptPayment = false
    state.hasUserModifiedSlug = false
    state.isGeneratingSlug = false
    state.slugValidationResult = null
    initializeEditor()
  },

  /**
   * Handle manual slug input changes
   */
  handleSlugChange(newSlug: string) {
    // Mark as user-modified only if this change is not from auto-generation
    if (!state.isGeneratingSlug) {
      // If user clears the slug field, reset the modification flag to allow auto-generation
      if (!newSlug?.trim()) {
        state.hasUserModifiedSlug = false
      } else {
        state.hasUserModifiedSlug = true
      }
    }
    state.formData.slug = newSlug.toLowerCase()
    // Clear validation errors when user manually changes slug
    state.slugValidationResult = null
  },
  async handleAdvanced() {
    // Store form data temporarily and navigate to advanced settings
    // Do NOT create the calendar yet - it will be created on Save

    const calendarName = state.formData.name?.trim() || ''

    let slug = state.formData.slug?.trim()

    const hours =
      props.selectedCalendarType === CalendarType.EVENT
        ? availabilityPickerRef.value?.methods?.getAvailability() || []
        : []

    const description = state.shouldShowDescription
      ? state.formData.description
      : ''
    const sanitizedDescription = DOMPurify.sanitize(description, {
      ADD_ATTR: ALLOWED_ATTR_FOR_DESCRIPTION,
    })

    // Handle team members - convert to proper structure for non-EVENT types
    let teamMembers = state.formData.teamMembers

    const allUsers = (AppState.globalUsers as any)?.users || []

    // Helper function to create a properly structured team member with user's preferred meeting location
    const createTeamMember = (userId: string, isPrimary: boolean = false) => {
      const teamMemberDetail = allUsers.find(x => x.id === userId)

      const zoomConnection = state.zoomConnections.find(
        connection => connection.ownerId === userId
      )
      const msTeamsConnection = state.msTeamsConnections.find(
        connection => connection.ownerId === userId
      )

      const selectedCustomMeetingLocation =
        teamMemberDetail?.locationWiseMeetingLocation &&
        teamMemberDetail.locationWiseMeetingLocation[AppState.locationId]
      const selectedIsZoomAdded =
        teamMemberDetail?.locationWiseZoomAdded &&
        teamMemberDetail.locationWiseZoomAdded[AppState.locationId]

      // Determine the kind based on user's preferred meeting location type
      // selectedIsZoomAdded values: 'true' (zoom), 'meet' (google), 'lc_meet', 'ms_teams', 'phone', 'address', 'false'/'custom' (custom)
      let kind = 'custom'
      if (props.selectedCalendarType !== CalendarType.CLASS_BOOKING) {
        kind = meetingKindFromStaffDefault(selectedIsZoomAdded)
      }

      // For conference types, location should be empty as meeting links are auto-generated
      const isConferenceType = isVideoConferenceKind(kind)
      const meetingLocation = isConferenceType
        ? ''
        : state.formData.meetingLocation || selectedCustomMeetingLocation || ''

      const isClassBooking =
        props.selectedCalendarType === CalendarType.CLASS_BOOKING

      const zoomOauthId = zoomConnection?.oauthId
      const msTeamsOauthId = msTeamsConnection?.oauthId

      return {
        userId,
        priority: 0.5,
        isZoomAdded: isClassBooking ? 'false' : selectedIsZoomAdded || 'false',
        ...(zoomOauthId && { zoomOauthId }),
        ...(msTeamsOauthId && { msTeamsOauthId }),
        ...(meetingLocation && { meetingLocation }),
        selected: true,
        isPrimary,
        locationConfigurations: [
          {
            kind,
            location: meetingLocation,
            position: 0,
            meetingId: `${kind}_0`,
            zoomOauthId:
              kind === 'zoom_conference' ? zoomOauthId || '' : undefined,
            msTeamsOauthId:
              kind === 'ms_teams_conference' ? msTeamsOauthId || '' : undefined,
          },
        ],
      }
    }

    if (props.selectedCalendarType !== CalendarType.EVENT) {
      // If team members were selected, convert user IDs to full team member objects
      if (Array.isArray(teamMembers) && teamMembers.length > 0) {
        teamMembers = teamMembers.map((userId: string, index: number) =>
          createTeamMember(userId, index === 0)
        ) as any
      } else {
        if (props.selectedCalendarType === CalendarType.PERSONAL) {
          // PERSONAL calendars: default to current user
          const defaultUser = allUsers.find(x => x.id === AppState.user?.id)
          if (defaultUser) {
            teamMembers = [createTeamMember(defaultUser.id, true)] as any
          }
        }
        // For other calendar types (ROUND_ROBIN, SERVICE, CLASS_BOOKING): don't auto-assign team members
      }
    }

    // Store draft data in AppState (to be used by advanced settings)
    AppState.draftCalendarData = {
      name: calendarName,
      description: sanitizedDescription,
      eventTitle: '{{contact.name}}',
      openHours: hours,
      slotInterval:
        props.selectedCalendarType === CalendarType.SERVICE ? 15 : 30,
      slotIntervalUnit: 'mins',
      slotDuration:
        state.formData.slotDurationUnit === 'hours'
          ? Number(state.formData.slotDuration) * 60
          : Number(state.formData.slotDuration),
      slotDurationUnit: state.formData.slotDurationUnit,
      widgetSlug: slug,
      locationId: AppState.locationId,
      calendarType: props.selectedCalendarType,
      showSeatsPerSlot:
        props.selectedCalendarType === CalendarType.CLASS_BOOKING,
      meetingLocation: state.formData.meetingLocation || '',
      teamMembers: teamMembers,
      appoinmentPerSlot:
        props.selectedCalendarType === CalendarType.CLASS_BOOKING
          ? Number(state.formData.appoinmentPerSlot) || 1
          : undefined,
      shouldAcceptPayment: state.shouldAcceptPayment,
      stripe:
        state.shouldAcceptPayment && state.formData.stripe.amount
          ? {
              amount: Number(state.formData.stripe.amount),
              currency: state.formData.stripe.currency,
              chargeDescription: state.formData.stripe.chargeDescription,
            }
          : {},
      // Store default schedules for later use
      teamMembersDefaultSchedule: teamMembersDefaultSchedule.value,
    }

    // Navigate to advanced settings in "create mode"
    AppState.isNewCalendar = true
    AppState.selectedCalendarId = '' // No calendar ID yet
    toggleAdvancedView()
    AppState.showCalendarLinkModal = false

    // Reset the modal
    methods.resetInputs()
  },
  handleCancel() {
    methods.resetInputs()
    emit('onModalClose')
    isInitialLoad.value = true
  },
  async selectDefaultTeamMember() {
    const filteredTeamMember = usersInLocationOptions.value.find(
      x => x.value === AppState.user?.id
    )?.value
    state.formData.teamMembers = filteredTeamMember
      ? ([filteredTeamMember] as any)
      : []
    isInitialLoad.value = false
  },
}

// Create debounced function for generating slug
const debouncedGenerateSlug = debounce(methods.generateSlugFromName, 800)

// Watch for calendar name changes to auto-generate slug
watch(
  () => state.formData.name,
  newName => {
    if (newName?.trim()) {
      debouncedGenerateSlug(newName)
    }
  }
)

watch(
  () => [usersInLocationOptions.value, state.formData?.teamMembers],

  usersInLocationOptions => {
    if (
      usersInLocationOptions.length > 0 &&
      state.formData?.teamMembers?.length === 0 &&
      props.selectedCalendarType === CalendarType.PERSONAL &&
      isInitialLoad.value
    ) {
      methods.selectDefaultTeamMember()
    }
  },
  { deep: true, immediate: true }
)

watch(
  () => props.selectedCalendarType,
  newValue => {
    if (newValue === CalendarType.SERVICE) {
      state.formData.slotDuration = '15'
    }
  }
)

const initializeEditor = () => {
  editor.value = new Editor({
    content: state.formData.description || '',
    parseOptions: {
      preserveWhitespace: 'full',
    },
    extensions: [
      History,
      RequiredExtensions,
      AllExtensions.configure({
        placeholder: {
          placeholder: t('create_calendar.description_placeholder'),
          showForAllNodes: false,
        },
        divTag: {
          inline: false,
          preserveWhitespace: false,
          HTMLAttributes: {
            style: 'color:#10182899',
          },
        },
        preTag: {
          inline: false,
          preserveWhitespace: false,
          HTMLAttributes: {
            style: 'color:#10182899',
          },
        },
        textAlignment: { types: ['heading', 'paragraph'] },
        paragraph: {
          HTMLAttributes: {
            style: 'margin:0px;color:#10182899',
          },
        },
        heading: {
          HTMLAttributes: {
            style: 'color:#10182899',
          },
        },
      }),
    ],
    onUpdate({ editor }) {
      state.formData.description = editor.getHTML()
    },
  })
}

onMounted(() => {
  resetCalendarEditValidationData()
  initializeEditor()
  methods.loadData()
})

const isServiceCalendar = computed(() => {
  return props.selectedCalendarType === CalendarType.SERVICE
})

const showTeamMemberSelection = computed(() => {
  return (
    props.selectedCalendarType === CalendarType.ROUND_ROBIN ||
    props.selectedCalendarType === CalendarType.CLASS_BOOKING ||
    props.selectedCalendarType === CalendarType.COLLECTIVE ||
    props.selectedCalendarType === CalendarType.SERVICE ||
    props.selectedCalendarType === CalendarType.PERSONAL
  )
})

const selectTeamMemberLabel = computed(() => {
  return props.selectedCalendarType === CalendarType.SERVICE
    ? t('create_calendar.service_select_team_member')
    : props.selectedCalendarType === CalendarType.ROUND_ROBIN ||
      props.selectedCalendarType === CalendarType.COLLECTIVE
    ? t('create_calendar.select_team_member')
    : t('create_calendar.select_single_team_member')
})

const teamMembersForSchedule = computed(() => {
  return state.formData.teamMembers.map(x => {
    const user = (AppState.globalUsers as any)?.users.find(y => y.id === x)
    return {
      ...user,
      availabilityText: getAvailabilityText(
        teamMembersDefaultSchedule.value[x]
      ),
    }
  })
})
</script>

<template>
  <UIModal
    id="generalConfirmationModal"
    :width="680"
    :show="show"
    @update:show="val => $emit('update:show', val)"
  >
    <template #header>
      <UIModalHeader
        id="generalConfirmationModalHeader"
        type="primary"
        :title="t('create_calendar.new_calendar')"
        @close="methods.handleCancel"
      />
    </template>

    <div class="w-full">
      <UIForm
        id="create-calendar-form"
        ref="formRef"
        :model="state.formData"
        :rules="rules"
        class="w-full"
      >
        <UIFormItem
          :label="
            isServiceCalendar
              ? $t('create_calendar.service_calendar_name')
              : $t('create_calendar.calendar_name')
          "
          path="name"
          :tooltip="getTooltip($t('create_calendar.calendar_name_tooltip'))"
        >
          <UIInput
            id="calendar-name-input"
            v-model="state.formData.name"
            :placeholder="$t('create_calendar.eg_outbound_reach')"
          />
        </UIFormItem>

        <UIButton
          id="add-description"
          :text="true"
          class="mb-6 border-none"
          :aria-label="
            state.shouldShowDescription
              ? $t('create_calendar.remove_description')
              : $t('create_calendar.add_description')
          "
          @click="state.shouldShowDescription = !state.shouldShowDescription"
        >
          <PlusIcon v-if="!state.shouldShowDescription" class="mr-2 h-5 w-5" />

          <MinusIcon v-else class="mr-2 h-5 w-5" />

          {{
            state.shouldShowDescription
              ? t('create_calendar.remove_description')
              : t('create_calendar.add_description')
          }}
        </UIButton>

        <UIFormItem
          v-if="state.shouldShowDescription"
          :label="$t('create_calendar.description')"
        >
          <div class="flex w-full flex-col">
            <TextEditorMenu :editor="editor" form-id="#create-calendar-form" />
            <UIRichTextEditor
              :editor="editor"
              container-class="force-border-0 overflow-x-auto custom-editor-styling"
            />
          </div>
        </UIFormItem>

        <UIFormItem
          v-if="showTeamMemberSelection"
          :label="selectTeamMemberLabel"
          path="teamMembers"
          :tooltip="
            getTooltip($t('create_calendar.select_team_member_tooltip'))
          "
        >
          <UISelect
            id="calendar-team-member-select-modal"
            v-model:value="state.formData.teamMembers"
            :multiple="true"
            :filterable="true"
            :options="usersInLocationOptions"
          />
        </UIFormItem>

        <UIFormItem
          :label="$t('create_calendar.custom_url')"
          path="slug"
          :tooltip="getTooltip(t('create_calendar.slug_tooltip'))"
          class="border-y pb-4 pt-4"
          :show-feedback="false"
        >
          <div class="flex w-full flex-col gap-1">
            <UIInputGroup class="w-full">
              <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
              <UIInputGroupLabel>/widget/bookings/</UIInputGroupLabel>
              <UIInput
                id="calendarUrl"
                :model-value="state.formData.slug"
                :placeholder="t('settings.calendar.my_calendar')"
                @update:model-value="methods.handleSlugChange($event)"
                :class="[
                  'flex-1',
                  {
                    'border !border-red-500':
                      state.slugValidationResult?.message,
                  },
                ]"
              />
            </UIInputGroup>
            <div
              class="text-red-500"
              v-if="!state.slugValidationResult?.isValid"
            >
              {{ state.slugValidationResult?.message }}
            </div>
          </div>
        </UIFormItem>

        <div class="mt-4 flex space-x-6">
          <UIFormItem
            :label="
              selectedCalendarType === CalendarType.SERVICE
                ? $t('calendar_advanced.availability.service_duration')
                : $t('create_calendar.meeting_duration')
            "
            :tooltip="
              getTooltip($t('create_calendar.meeting_duration_tooltip'))
            "
            class="w-2/3"
            path="slotDuration"
          >
            <UIInputGroup>
              <UIInput
                id="meeting-duration-input"
                v-model="state.formData.slotDuration"
                placeholder=""
              />
              <UISelect
                id="meeting-duration-select"
                v-model:value="state.formData.slotDurationUnit"
                class="w-60"
                :options="slotDurationUnitOptions"
              />
            </UIInputGroup>
          </UIFormItem>

          <UIFormItem
            v-if="selectedCalendarType === CalendarType.CLASS_BOOKING"
            :label="$t('create_calendar.seats_per_class')"
            :tooltip="getTooltip(t('create_calendar.seats_per_class_tooltip'))"
            class="w-1/3"
            path="appoinmentPerSlot"
          >
            <UIInput
              id="appointment-per-slot-input"
              v-model="state.formData.appoinmentPerSlot"
              placeholder=""
            />
          </UIFormItem>
        </div>
        <!-- availability picker -->
        <div class="border-b pb-2">
          <div v-if="selectedCalendarType !== CalendarType.EVENT">
            <SchedulePicker
              v-if="teamMembersForSchedule?.length"
              :team-members="teamMembersForSchedule"
              class="border-t-2 border-gray-200 pt-4"
            />
            <UIButton
              v-if="teamMembersForSchedule.length > 4"
              id="show-more-schedule-button"
              :text="true"
              :disabled="state.isSaving"
              class="mb-2"
              @click="methods.handleAdvanced"
            >
              {{ $t('create_calendar.show_more_schedule') }}
            </UIButton>
            <div class="text-sm text-gray-500">
              {{ $t('create_calendar.show_more_helper_text') }}
            </div>
          </div>
          <AvailabilityPicker
            v-show="selectedCalendarType === CalendarType.EVENT"
            ref="availabilityPickerRef"
          />
        </div>

        <UIFormItem
          v-if="
            selectedCalendarType === CalendarType.EVENT ||
            selectedCalendarType === CalendarType.CLASS_BOOKING
          "
          :label="$t('calendar_advanced.meeting_location.meeting_location')"
          :tooltip="getTooltip(t('create_calendar.meeting_location_tooltip'))"
          path="meetingLocation"
          class="mt-5 w-64"
        >
          <UIInput
            id="calendar-event-title-input"
            v-model="state.formData.meetingLocation"
            :placeholder="$t('appointment_modal.enter_meeting_location')"
          />
        </UIFormItem>
        <div v-if="shouldAllowPayment" class="">
          <div class="flex items-center space-x-3 pb-3 pt-4">
            <UISwitch
              v-model:value="state.shouldAcceptPayment"
              :aria-label="$t('create_calendar.aria.should_accept_payment')"
              size="small"
            />
            <div class="font-normal">
              {{ $t('create_calendar.accept_payments') }}
            </div>
          </div>
          <div v-if="state.shouldAcceptPayment" class="">
            <UIFormItem
              :label="$t('create_calendar.amount')"
              class="mt-4 w-full"
              path="stripeAmount"
            >
              <UIInputGroup>
                <UIInputNumber
                  id="calendars-stripe-amount-input"
                  v-model="state.formData.stripe.amount"
                  placeholder=""
                  :min="0"
                  :max="999999.99"
                  :show-button="false"
                  size="large"
                  class="w-full"
                />
                <UISelect
                  id="calendar-stripe-amount-currency-select"
                  v-model:value="state.formData.stripe.currency"
                  class="w-32"
                  :options="paymentCurrencyOptions"
                />
              </UIInputGroup>
            </UIFormItem>

            <UIFormItem :label="$t('create_calendar.description')">
              <UIInput
                id="calendar-stripe-charge-input"
                v-model="state.formData.stripe.chargeDescription"
                type="textarea"
                :placeholder="$t('create_calendar.payment_placeholder')"
                :rows="4"
              />
            </UIFormItem>
          </div>
        </div>
      </UIForm>
    </div>
    <template #footer>
      <div class="mt-9 flex items-center justify-between">
        <UIButton
          id="advanced-calendar-button"
          :text="true"
          :disabled="state.isSaving"
          @click="methods.handleAdvanced"
        >
          <Settings01Icon class="mr-2 h-5 w-5" />
          {{ $t('create_calendar.advanced_settings') }}
        </UIButton>
        <div class="flex space-x-2">
          <UIButton
            id="cancel-calendar-button"
            type="default"
            class="relative bottom-1"
            @click="methods.handleCancel"
          >
            {{ $t('create_calendar.cancel') }}
          </UIButton>
          <UIButton
            id="create-calendar-button"
            type="primary"
            class="relative bottom-1"
            :disabled="state.isSlugValidating || state.isSaving"
            @click="methods.handleSave"
          >
            {{ $t('create_calendar.confirm') }}
          </UIButton>
        </div>
      </div>
    </template>
  </UIModal>
</template>

<style lang="scss">
#create-calendar-form {
  .force-border-0 {
    border: 1px solid #d0d5dd !important;
    padding: 15px !important;
    max-height: 200px !important;
    min-height: 100px !important;
  }

  .ProseMirror p {
    margin: 0;
    font-size: 13px !important;
  }

  .ProseMirror ol {
    font-size: 14px !important;
  }

  .v3-search input::placeholder {
    font-size: 1rem;
    text-align: left;
    font-weight: normal;
    font-size: 0.875rem;
  }
}
</style>
